<template>
  <section id="section__find-id" class="view-container">
    <v-form @submit.prevent="find(phone)">
      <v-row no-gutters class="mt-10 mb-10">
        <v-col cols="12">
          <p class="font--m-1">비밀번호 찾기</p>
          <router-link
            class="color--black"
            :to="{ path: '/auth/find/id' }"
            id="btn__PxKyeTAAlUiM+FaViqUZMw=="
          >
            <p class="font--m-4">아이디 찾기</p>
          </router-link>
        </v-col>
      </v-row>

      <div v-if="!resetParams.verification.isValid">
        <v-row no-gutters class="mt-10 mb-10">
          <v-col cols="12">
            <p class="text-center font--m-5">
              회원 가입시 등록하신 <br />
              이메일 주소와 휴대폰 번호를 입력해 주세요.
            </p>
            <p class="mb-1 text-left">이메일</p>
            <v-text-field
              v-model="user.email"
              solo
              single-line
              hide-details
              outlined
              flat
              :error="!!invalidMsg.email"
              placeholder="예) abc@gmail.com"
            ></v-text-field>
            <ErrorText :text="invalidMsg.email" nudgeLeft="4" />
            <p class="mt-3 mb-1 text-left">휴대폰 번호</p>
            <v-text-field
              v-model="user.phone"
              solo
              single-line
              hide-details
              outlined
              flat
              :error="!!invalidMsg.phone"
              type="tel"
              placeholder="예) 01012345678"
              oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')"
            >
              <template v-slot:append>
                <v-fade-transition leave-absolute>
                  <BasicButton
                    id="btn__gQEcAvmYFE61hrcx6ORrUw=="
                    height="36px"
                    @click="find(user)"
                    :disabled="isExpireTimeOut || !phoneIsValid"
                    :loading="receiveLoading"
                  >
                    인증번호 받기
                  </BasicButton>
                </v-fade-transition>
              </template>
            </v-text-field>
            <ErrorText :text="invalidMsg.phone" nudgeLeft="4" />
            <div v-if="resetParams.verification.expirationTime">
              <p class="mt-3 mb-1 text-left">인증번호</p>
              <v-text-field
                v-model="resetParams.verification.verificationCode"
                solo
                single-line
                hide-details
                outlined
                flat
              >
                <template v-slot:append>
                  <v-fade-transition leave-absolute>
                    <BasicButton
                      id="btn__kaQsCkJy1kS2mW3jLDr9Vg=="
                      height="36px"
                      @click="resetConfirm(resetParams)"
                      :loading="confirmLoading"
                      :disabled="confirmLoading"
                    >
                      인증번호 확인
                    </BasicButton>
                  </v-fade-transition>
                </template>
              </v-text-field>
              <ExpireTime
                v-if="passwordResetTimeoutDate"
                :expire-date="passwordResetTimeoutDate"
                @emitTimeOver="isTimeOut"
              ></ExpireTime>
            </div>
          </v-col>
        </v-row>
      </div>

      <div v-else-if="resetParams.verification.isValid">
        <v-row no-gutters class="mt-10 mb-10">
          <v-col cols="12">
            <p class="mt-3 mb-1 text-left">새로운 비밀번호 설정</p>
            <v-text-field
              v-model="resetParams.newPassword1"
              solo
              single-line
              hide-details
              outlined
              flat
              :error="!!invalidMsg.newPassword1"
              :rules="rules.newPassword1"
              autocomplete="on"
              label="새로운 비밀번호 입력"
              :append-icon="passwordShow ? 'mdi-eye' : 'mdi-eye-off'"
              :type="passwordShow ? 'text' : 'password'"
              @click:append="passwordShow = !passwordShow"
            ></v-text-field>
            <ErrorText :text="invalidMsg.newPassword1" nudgeLeft="4" />
            <v-text-field
              v-model="resetParams.newPassword2"
              solo
              single-line
              hide-details
              outlined
              flat
              :rules="rules.newPassword2"
              :error="!!invalidMsg.newPassword2"
              autocomplete="on"
              label="새로운 비밀번호 확인"
              :append-icon="passwordConfirmShow ? 'mdi-eye' : 'mdi-eye-off'"
              :type="passwordConfirmShow ? 'text' : 'password'"
              @click:append="passwordConfirmShow = !passwordConfirmShow"
            ></v-text-field>
            <ErrorText :text="invalidMsg.newPassword2" nudgeLeft="4" />
          </v-col>
        </v-row>

        <v-row no-gutters class="mt-10 mb-10">
          <v-col cols="12">
            <BasicButton
              id="btn__ef8wsnUsPkeHNGWUyhuqug=="
              :disabled="isLoading"
              :loading="isLoading"
              @click="resetChange(resetParams)"
            >
              비밀번호 재설정하기
            </BasicButton>
          </v-col>
        </v-row>
      </div>
    </v-form>
  </section>
</template>

<script>
import Regex from '@/const/Regex'
import ExpireTime from '@/components/auth/ExpireTime.vue'

import { mapActions } from 'vuex'

export default {
  components: {
    ExpireTime,
  },
  data() {
    return {
      user: {
        email: null,
        phone: null,
      },
      resetParams: {
        email: null,
        phone: null,
        newPassword1: null,
        newPassword2: null,
        verification: {
          phone: null,
          expirationTime: null,
          token: null,
          isExpired: null,
          isValid: null,
        },
      },
      rules: {
        email: [
          (value) => !!value || '이메일을 입력해주세요',
          (value) => Regex.EMAIL.REGEX.test(value) || Regex.EMAIL.MSG,
        ],
        phone: [
          (value) => !!value || '휴대폰번호를 입력해주세요',
          (value) => Regex.PHONE.REGEX.test(value) || Regex.PHONE.MSG,
        ],
        newPassword1: [
          (value) => !!value || '비밀번호를 입력해주세요',
          (value) => Regex.PASSWORD.REGEX.test(value) || Regex.PASSWORD.MSG,
        ],
        newPassword2: [
          (value) => value === this.resetParams.newPassword1 || '비밀번호가 일치하지 않습니다',
        ],
      },
      invalidMsg: {
        newPassword1: null,
        newPassword2: null,
        email: null,
        phone: null,
      },
      emailIsValid: false,
      phoneIsValid: false,
      newPassword1IsValid: false,
      newPassword2IsValid: false,
      isLoading: false,
      receiveLoading: false,
      confirmLoading: false,
      isExpireTimeOut: false,
      passwordResetTimeoutDate: null,
      passwordShow: false,
      passwordConfirmShow: false,
    }
  },
  computed: {
    isValidAll() {
      return (
        this.newPassword1IsValid === true &&
        this.newPassword2IsValid === true &&
        this.phoneIsValid === true &&
        this.emailIsValid === true
      )
    },
  },
  watch: {
    'user': {
      deep: true,
      handler(v) {
        this.resetParams.email = v.email
        this.resetParams.phone = v.phone
        this.resetParams.verification.phone = v.phone
      },
    },
    'resetParams.email'(v) {
      this.emailDuplicate(v)
    },
    'resetParams.phone'(v) {
      this.phoneDuplicate(v)
    },
    'resetParams.newPassword1'(v) {
      const msg = this.getFormErrMsg('newPassword1', v)
      this.newPassword1IsValid = msg === null
    },
    'resetParams.newPassword2'(v) {
      const msg = this.getFormErrMsg('newPassword2', v)
      this.newPassword2IsValid = msg === null
    },
    'isValidAll': {
      immediate: true,
      handler(v) {
        if (v === true) {
          this.$emit('update:resetParms', this.resetParams)
        } else {
          this.$emit('update:resetParams', null)
        }
      },
    },
    'passwordResetTimeoutDate'(v) {
      if (v) {
        this.isExpireTimeOut = true
      } else {
        this.isExpireTimeOut = false
      }
    },
  },
  created() {},
  methods: {
    ...mapActions('auth', [
      'requestPasswordReset',
      'requestPasswordResetConfirm',
      'requestPasswordResetChange',
    ]),
    phoneDuplicate(phone) {
      const msg = this.getFormErrMsg('phone', phone)
      if (msg === null) {
        this.phoneIsValid = true
      } else {
        this.phoneIsValid = false
      }
    },
    emailDuplicate(email) {
      const msg = this.getFormErrMsg('email', email)
      if (msg === null) {
        this.emailIsValid = true
      } else {
        this.emailIsValid = false
      }
    },
    insertInvalidMsg(type, msg) {
      this.invalidMsg[type] = msg
    },
    async isTimeOut() {
      const swal = await this.$swal.basic.alert({
        html: '발급된 인증번호의 유효기간이 만료되었습니다. <br />다시 입력해주세요.',
      })
      this.passwordResetTimeoutDate = null
    },
    getFormErrMsg(type, newVal) {
      const rules = this.rules[type]
      let msg = null
      if (newVal) {
        for (let index = 0; index < rules.length; index++) {
          const rule = rules[index]
          const isValid = rule(newVal)
          if (typeof isValid !== 'boolean') {
            msg = isValid
            break
          }
        }
      }
      this.insertInvalidMsg(type, msg)
      return msg
    },
    async find(user) {
      this.receiveLoading = true
      try {
        const res = await this.requestPasswordReset(user)
        this.resetParams.verification = Object.assign(this.resetParams.verification, res)
        this.passwordResetTimeoutDate = new Date(this.resetParams.verification.expirationTime)
      } catch (e) {
        if (
          e.status === 400 &&
          (e.message === '입력하신 이메일에 등록된 휴대폰 번호가 일치하지 않습니다.' ||
            e.message === '입력하신 정보로 가입한 아이디가 존재하지 않습니다.')
        ) {
          await this.$swal.basic.error('아이디 또는 입력하신 핸드폰 번호가 일치하지 않습니다.')
          return
        }
        throw e
      } finally {
        this.receiveLoading = false
      }
    },
    async resetConfirm(resetParams) {
      this.confirmLoading = true
      try {
        this.resetParams.verification = await this.requestPasswordResetConfirm(
          resetParams.verification,
        )
        await this.$swal.basic.success('인증이 완료되었습니다. 비밀번호를 재설정해주세요.')
      } finally {
        this.confirmLoading = false
      }
    },
    async resetChange(resetParams) {
      this.isLoading = true
      try {
        await this.requestPasswordResetChange(resetParams)
        const alert = await this.$swal.basic.success({
          html: `비밀번호 재설정이 완료되었습니다. <br /> 새로운 비밀번호로 로그인 해주세요.`,
          confirmButtonText: '로그인 하러가기',
        })
        this.passwordResetTimeoutDate = null
        if (alert.isConfirmed === true) {
          await this.$router.push('/signin')
        }
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
